// src/pages/ArticleGenerator/ArticleGenerator.jsx                                                                                                                
import React, { useState, useEffect, useRef } from 'react';                                                                                                       
import { useParams, useNavigate } from 'react-router-dom';                                                                                                        
import { useTranslation } from 'react-i18next';                                                                                                                   
import { useAuth } from '../../contexts/AuthContext';                                                                                                             
import DocumentSummaryAccordion from '../../components/Article/DocumentSummaryAccordion';                                                                                                                                                                                               
import {
    FileUp, Loader2, AlertCircle, Plus, Trash2,
    Download, Eye, Edit, ChevronLeft, ChevronRight,
    CheckCircle, XCircle, Sparkles, RefreshCw,
    Copy, Check, BookOpen, X
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import {
    createArticleProject,
    uploadDocuments,
    getProjectStatus,
    addSection,
    updateSection,
    deleteSection,
    generateArticle,
    downloadArticle,
    suggestTitles
} from '../../services/chatApi';
import { Link } from 'react-router-dom';



const INSTRUCTION_BASE_PATH = '/instructions/articleGenerator';



const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};




const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};


const ProgressBar = ({ progress, message }) => (
    <div className="w-full max-w-md mx-auto mt-6">
        <div className="flex justify-between mb-2">
            <span className="text-sm font-medium text-brand-orange animate-pulse">
                {message || 'Processing...'}
            </span>
            <span className="text-sm font-bold text-gray-600">{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
            <div
                className="bg-brand-orange h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${Math.max(5, progress)}%` }} // Min width 5% for visibility
            ></div>
        </div>
    </div>
);



const removeConsecutiveDuplicateLines = (markdownString) => {
  if (!markdownString) return markdownString;

  const lines = markdownString.split('\n');
  const uniqueLines = [];
  let previousLine = null;

  for (const line of lines) {
    // Compare the trimmed version of the current line with the previous one
    if (line.trim() !== previousLine?.trim()) {
      uniqueLines.push(line);
      previousLine = line;
    }
    // Optionally, you could add a condition here to ignore empty lines
    // if (line.trim() !== '') { ... }
  }

  return uniqueLines.join('\n');
};

const InstructionSidebar = ({ isOpen, onClose, t, i18n }) => {
    const [instructionContent, setInstructionContent] = useState('');
    const [loadingInstructions, setLoadingInstructions] = useState(true);
    const [errorInstructions, setErrorInstructions] = useState(null);

    useEffect(() => {
        if (!isOpen) return; // Only fetch when opened

        const fetchInstructions = async () => {
            const langCode = i18n.language.split('-')[0];
            const filePath = `${INSTRUCTION_BASE_PATH}_${langCode}.md`;
            setLoadingInstructions(true);

            try {
                const response = await fetch(filePath);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const text = await response.text();
                setInstructionContent(text);
            } catch (err) {
                console.error("Error fetching instructions:", err);
                setErrorInstructions(err.message);
            } finally {
                setLoadingInstructions(false);
            }
        };

        fetchInstructions();
    }, [i18n.language, isOpen]);

    return (
        <div className={`fixed inset-y-0 right-0 w-80 md:w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50 flex flex-col border-l border-gray-200 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="p-4 bg-brand-orange text-white flex justify-between items-center shadow-sm">
                <h3 className="font-semibold text-lg">{t('articleGenerator.instructions', 'Instructions')}</h3>
                <button onClick={onClose} className="p-1 hover:bg-orange-600 rounded-full transition-colors">
                    <X className="w-5 h-5" />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
                {loadingInstructions && (
                    <div className="flex justify-center py-4"><Loader2 className="animate-spin text-brand-orange" /></div>
                )}
                {errorInstructions && (
                    <p className="text-red-500 text-sm">Error: {errorInstructions}</p>
                )}
                {!loadingInstructions && !errorInstructions && (
                    <div className="prose prose-sm prose-orange max-w-none text-brand-dark">
                        <ReactMarkdown
				components={{
      				  // Style unordered lists (bullets)
        			ul: ({node, ...props}) => <ul className="list-disc pl-5 mb-4 space-y-1" {...props} />,
        			// Style ordered lists (numbers)
        			ol: ({node, ...props}) => <ol className="list-decimal pl-5 mb-4 space-y-1" {...props} />,
        			// Style list items
        			li: ({node, ...props}) => <li className="pl-1" {...props} />
    				}}
			>{instructionContent}</ReactMarkdown>
                    </div>
                )}
            </div>
        </div>
    );
};
const ArticleGenerator = () => {
    const { t, i18n } = useTranslation();
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();

    // Project state
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Upload state
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    // Title suggestions state
    const [titleSuggestions, setTitleSuggestions] = useState([]);
    const [loadingTitles, setLoadingTitles] = useState(false);
    const [showTitleSuggestions, setShowTitleSuggestions] = useState(false);

    // UI state
    const [activeStep, setActiveStep] = useState(0);
    const pollingIntervalRef = useRef(null);
    const [editingSection, setEditingSection] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, sectionId: null });

    // State for pending sections (to be added after project creation)
    const [pendingSections, setPendingSections] = useState([]);

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);



    const steps = [
        t('articleGenerator.uploadDocuments'),
        t('articleGenerator.configureArticle'),
        t('articleGenerator.generateExport')
    ];

    const clearPollingInterval = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    // Initialize or load project
    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/login');
            return;
        }
        if (projectId) {
            loadProject();
        }

        return () => {
            clearPollingInterval();
        };
    }, [projectId, user, authLoading, navigate]);

    const startPolling = () => {
        clearPollingInterval();
        pollingIntervalRef.current = setInterval(async () => {
            try {
                const updatedProject = await getProjectStatus(projectId);
                setProject(updatedProject);
                if (updatedProject.status === 'READY') {
                    clearPollingInterval();
                    setActiveStep(1);
                    if (updatedProject.article_plan && !titleSuggestions.length) {
                        fetchTitleSuggestions();
                    }
                } else if (updatedProject.status === 'COMPLETED') {
                    clearPollingInterval();
                    setActiveStep(2);
                } else if (updatedProject.status === 'FAILED') {
                    clearPollingInterval();
                    setError(updatedProject.error_message);
                }
            } catch (err) {
                console.error('Polling error:', err);
                clearPollingInterval();
            }
        }, 3000);
    };



    const loadProject = async () => {
        try {
            setLoading(true);
            const projectData = await getProjectStatus(projectId);
            setProject(projectData);
            if (projectData.status === 'UPLOADING' || projectData.status === 'PROCESSING') {
                setActiveStep(0);
                startPolling();
            } else if (projectData.status === 'READY') {
                setActiveStep(1);
                // if (projectData.article_plan) {
                //    fetchTitleSuggestions();
                //}
            } else if (projectData.status === 'GENERATING') {
                setActiveStep(2);
                startPolling();
            } else if (projectData.status === 'COMPLETED') {
                setActiveStep(2);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchTitleSuggestions = async () => {
        if (!projectId) return;
        try {
            setLoadingTitles(true);
            setError(null);
            const response = await suggestTitles(projectId);
            setTitleSuggestions(response.suggestions || []);
            setShowTitleSuggestions(true);
        } catch (err) {
            console.error('Error fetching title suggestions:', err);
        } finally {
            setLoadingTitles(false);
        }
    };

    const handleSelectTitle = (title) => {
        handleTitleChange(title);
        setShowTitleSuggestions(false);
    };

    const handleFileSelect = (e) => {
        const newFiles = Array.from(e.target.files);
        const MAX_FILES = 10;
        const MAX_SIZE = 10 * 1024 * 1024;

        // 1. Check size of the NEW files
        const oversized = newFiles.filter(f => f.size > MAX_SIZE);
        if (oversized.length > 0) {
            setError(t('articleGenerator.fileSizeError', { files: oversized.map(f => f.name).join(', ') }));
            e.target.value = ''; // Reset input
            return;
        }

        // 2. Combine existing files with new files
        // We check for duplicates by name to prevent adding the exact same file twice
        const updatedFiles = [...selectedFiles];
        newFiles.forEach(file => {
            if (!updatedFiles.some(existing => existing.name === file.name)) {
                updatedFiles.push(file);
            }
        });

        // 3. Check total count limit
        if (updatedFiles.length > MAX_FILES) {
            setError(t('articleGenerator.maxFilesError', { max: MAX_FILES }));
            e.target.value = ''; // Reset input
            return;
        }

        // 4. Update state and Clear Error
        setSelectedFiles(updatedFiles);
        setError(null);

        // 5. IMPORTANT: Reset the input value so the `onChange` event fires
        // even if you select the same file again (or select another file immediately)
        e.target.value = '';
    };

   const handleRemoveFile = (indexToRemove) => {
        setSelectedFiles(prevFiles => prevFiles.filter((_, idx) => idx !== indexToRemove));
    };


    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            setError(t('articleGenerator.selectFilesError'));
            return;
        }

        setIsUploading(true);
        setError(null);

        try {
            // STEP 1: CREATE THE PROJECT
            // We do this first to get the ID. We hold onto the result in a local variable.
            console.log("Creating a new project...");
            const initialProjectData = { title: t('articleGenerator.untitledArticle') };
            const newProject = await createArticleProject(initialProjectData);
            const newProjectId = newProject.id;

            if (!newProjectId) {
                throw new Error("Project creation failed to return a valid ID.");
            }
            console.log(`Project created with ID: ${newProjectId}`);

            // STEP 2: PREPARE AND UPLOAD THE DOCUMENTS
            // We use the newProjectId we just got. This happens BEFORE navigating.
            // The component has not re-rendered yet, so `selectedFiles` state is still perfectly valid.
            console.log(`Uploading documents to new project...`);
            const formData = new FormData();
            selectedFiles.forEach(file => {
                formData.append('files', file);
            });
            const language = i18n.language.split('-')[0];
            formData.append('language', language);

            const updatedProject = await uploadDocuments(newProjectId, formData);

            // STEP 3: UPDATE STATE AND NAVIGATE
            // Now that all the hard work is done, we update the UI.
            console.log("Upload accepted. Navigating and updating state...");
            setProject(updatedProject); // Update state with the "PROCESSING" project
            navigate(`/article-generator/${newProjectId}`, { replace: true }); // Now navigate

            // STEP 4: START POLLING
            // This is the final step.
            startPolling();

        } catch (err) {
            console.error("An error occurred during the create/upload process:", err);
            setError(err.message);
        } finally {
            setIsUploading(false);
        }
    };


    // Store section locally until project is created
    const handleAddNewSection = async (sectionData) => {
        setError(null);
        // Check if a project already exists by looking for projectId
        if (projectId) {
            try {
                // If project exists, call the API to add the section directly
                await addSection(projectId, sectionData);
                // After successfully adding, reload the project to get the updated sections list
                await loadProject();
            } catch (err) {
                // If the API call fails, show an error
                setError(err.message);
            }
        } else {
            // If no project exists yet, add the section to the temporary pending list
            setPendingSections(prev => [...prev, sectionData]);
        }
    };

    const handleTitleChange = async (newTitle) => {
        try {
            setProject(prev => ({ ...prev, title: newTitle }));
        } catch (err) {
            console.error('Error updating title:', err);
        }
    };

    const handleUpdateSection = async (sectionId, updates) => {
        try {
            await updateSection(projectId, sectionId, updates);
            await loadProject();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteSection = async (sectionId) => {
        setDeleteConfirmation({ isOpen: true, sectionId });
    };

    const confirmDeleteSection = async () => {
        if (deleteConfirmation.sectionId && projectId) {
            try {
                await deleteSection(projectId, deleteConfirmation.sectionId);
                await loadProject();
            } catch (err) {
                setError(err.message);
            } finally {
                setDeleteConfirmation({ isOpen: false, sectionId: null });
            }
        }
    };

    const cancelDeleteSection = () => {
        setDeleteConfirmation({ isOpen: false, sectionId: null });
    };

    const handleGenerate = async () => {
        if (!projectId) {
            setError(t('articleGenerator.noProjectError'));
            return;
        }
        try {
            setError(null);
            await generateArticle(projectId, {
                title: project.title,
                citation_style: project.citation_style,
                use_chain_of_thought: project.use_chain_of_thought,
                coherence_level: project.coherence_level,
                attempt: true
            });
            setProject(prev => ({ ...prev, status: 'GENERATING' }));
            startPolling();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDownload = async (format) => {
        if (!projectId) {
            setError(t('articleGenerator.noProjectError'));
            return;
        }
        try {
            const blob = await downloadArticle(projectId, format);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${project.title || 'article'}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading && !project) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-brand-orange" />
            </div>
        );
    }

    // if (user?.subscription_status !== 'active') {
    //     return <SubscribePrompt />;
    // }

    // if (user?.credits <= 0) {
    //     return <PurchaseCreditsPrompt />;
    // }

    // Check subscription and credits before rendering main content
    if (user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) {
        return (
            <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
                <SubscribePrompt />
            </div>
        );
    }
    if (user?.credits <= 0) {
        return (
            <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
                <PurchaseCreditsPrompt />
            </div>

         );
    }

    return (
        <div className="h-full flex flex-col bg-brand-white relative overflow-hidden">
            <InstructionSidebar
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                t={t}
                i18n={i18n}
            />
            {/* Header with Stepper */}
            <div className="bg-brand-white">
                <div className="max-w-7xl mx-auto px-4 py-4">

                    {project && (
                        <div className="flex items-center">
                            {steps.map((step, index) => (
                                <React.Fragment key={index}>
                                    <div className="flex items-center">
                                        <div className={`
                                            flex items-center justify-center w-8 h-8 rounded-full
                                            ${index <= activeStep
                                                ? 'bg-brand-orange text-black'
                                                : 'bg-gray-200 text-gray-500'}
                                        `}>
                                            {index < activeStep ? (
                                                <CheckCircle className="w-5 h-5" />
                                            ) : (
                                                <span>{index + 1}</span>
                                            )}
                                        </div>
                                        <span className={`ml-2 ${index === activeStep ? 'font-semibold text-black' : 'text-black'}`}>
                                            {step}
                                        </span>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className={`flex-1 h-1 mx-4 ${
                                            index < activeStep ? 'bg-brand-orange' : 'bg-gray-200'
                                        }`} />
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {/* Error Display */}
            {error && (
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                        <p className="text-red-700">{error}</p>
                    </div>
                </div>
            )}
            {/* Main Content */}
            <div className="flex-1 overflow-y-auto">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    {activeStep === 0 && <UploadStep
                        project={project}
                        selectedFiles={selectedFiles}
                        onFileSelect={handleFileSelect}
                        onRemoveFile={handleRemoveFile}
                        onUpload={handleUpload}
                        isUploading={isUploading}
                        pendingSections={pendingSections}
                        t={t}
                        isSidebarOpen={isSidebarOpen}
                        setIsSidebarOpen={setIsSidebarOpen}
                    />}
                    {activeStep === 1 && project && <ConfigureStep
                        project={project}
                        onTitleChange={handleTitleChange}
                        onAddSection={handleAddNewSection}
                        onUpdateSection={handleUpdateSection}
                        onDeleteSection={handleDeleteSection}
                        onNext={() => setActiveStep(2)}
                        titleSuggestions={titleSuggestions}
                        loadingTitles={loadingTitles}
                        showTitleSuggestions={showTitleSuggestions}
                        onToggleTitleSuggestions={() => setShowTitleSuggestions(!showTitleSuggestions)}
                        onSelectTitle={handleSelectTitle}
                        onRefreshTitles={fetchTitleSuggestions}
                        t={t}
                    />}
                    {activeStep === 2 && project && <GenerateStep
                        project={project}
                        onGenerate={handleGenerate}
                        onDownload={handleDownload}
                        onBack={() => {
                            // Set the local project status to indicate it's ready for configuration again
                            // This ensures the GenerateStep re-renders the configuration/review view
                            // if the user clicks "Proceed Generate" later.
                            setProject(prev => ({ ...prev, status: 'READY' }));
                            // Navigate back to the configuration step
                            setActiveStep(1);
                        }}
                        t={t}
                    />}
                </div>
            </div>
            {deleteConfirmation.isOpen && project && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold mb-4">{t('articleGenerator.confirmDelete')}</h3>
                        <p className="text-gray-600 mb-6">{t('articleGenerator.deleteConfirmMessage')}</p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={cancelDeleteSection}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                            >
                                {t('articleGenerator.cancel')}
                            </button>
                            <button
                                onClick={confirmDeleteSection}
                                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                            >
                                {t('articleGenerator.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {project && (
              <div className="border-t border-gray-200 bg-white">
                <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between">
                  {/* BACK */}
                  <button
                    onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                    disabled={activeStep === 0}
                    className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                  >
                    <ChevronLeft className="w-5 h-5 mr-1" />
                    {t('articleGenerator.back')}
                  </button>

                  {/* NEXT – ONLY ENABLED WHEN WE ARE REALLY READY */}
                  <button
                    onClick={async () => {
                      // -------------------------------------------------
                      // STEP 0 → 1 : upload just finished → start polling
                      // -------------------------------------------------
                      if (activeStep === 0 && project?.status === 'PROCESSING') {
                        startPolling();               // <-- starts interval
                        return;                       // **do NOT change step yet**
                      }

                      // -------------------------------------------------
                      // STEP 1 → 2 : configure → generate
                      // -------------------------------------------------
                      if (activeStep === 1) {
                        setActiveStep(2);
                        return;
                      }

                      // -------------------------------------------------
                      // ANY OTHER CASE – just let the step change (should never happen)
                      // -------------------------------------------------
                      setActiveStep(prev => Math.min(prev + 1, 2));
                    }}
                    disabled={
                      // Disable while we are still processing the upload
                      (activeStep === 0 && project?.status === 'PROCESSING') ||
                      // Disable the final step (no “next” after generate)
                      activeStep === 2
                    }
                    className="flex items-center px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 disabled:opacity-50"
                  >
                    {t('articleGenerator.next')}
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </button>
                </div>

              </div>
            )}

        </div>
    );
};

const UploadStep = ({ project, selectedFiles, onFileSelect, onRemoveFile, onUpload, 
    isUploading, pendingSections, t, isSidebarOpen, setIsSidebarOpen }) => {
    
    return (
        <div className="space-y-6 text-brand-dark">
            
            {/* Main container: Flexbox to put Text and Upload Box side-by-side */}
            <div className="flex flex-col md:flex-row gap-6 items-start">
                
                {/* LEFT SIDE: Title & Scrollable Description */}
                <div className="w-full md:w-1/2">
                    <div className="flex justify-between items-start mb-4">
                        <h2 className="text-2xl font-bold text-brand-orange">
                            {t('articleGenerator.generateDraftArticle')}
                        </h2>
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${isSidebarOpen ? 'bg-brand-orange text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                            <BookOpen className="w-4 h-4" />
                            <span className="text-sm font-medium">{t('articleGenerator.showInstructions', 'Instructions')}</span>
                        </button>
                    </div>

                    {/* Scrollable Container for Markdown Text */}
                    <div className="max-h-[400px] overflow-y-auto pr-2 border-r border-gray-100 prose prose-sm prose-orange">
                        <ReactMarkdown>
                            {t('articleGenerator.uploadInstructions')}
                        </ReactMarkdown>
                    </div>
                </div>

                {/* RIGHT SIDE: Upload Area */}
                <div className="w-full md:w-1/2">
                    {project?.status === 'PROCESSING' ? (
                        <div className="border-2 border-brand-orange rounded-lg p-8 text-center">
                            <Loader2 className="w-12 h-12 animate-spin text-brand-orange mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">{t('articleGenerator.processingDocuments')}</h3>
                            <ProgressBar
                                progress={project.generation_progress || 0}
                                message={project.current_step}
                            />
                            {project.documents && (
                                <div className="mt-4 text-sm text-gray-500">
                                    {t('articleGenerator.documentsUploaded', { count: project.documents.length })}
                                </div>
                            )}
                        </div>
                    ) : project?.status === 'READY' ? (
                        <div className="border-2 border-green-500 rounded-lg p-8 text-center">
                            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold mb-2">{t('articleGenerator.documentsReady')}</h3>
                            <p className="text-gray-600 mb-4">
                                {t('articleGenerator.documentsProcessed', { count: project.documents?.length })}
                            </p>
                            <div className="mt-6 space-y-3 max-h-60 overflow-y-auto">
                                {project.documents?.map(doc => (
                                    <div key={doc.id} className="bg-gray-50 rounded p-4 text-left">
                                        <h4 className="font-semibold text-sm">{doc.original_filename}</h4>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {doc.num_pages} {t('articleGenerator.pages')} • {doc.num_chunks} {t('articleGenerator.chunks')}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                            <input
                                type="file"
                                id="file-upload"
                                multiple
                                accept=".pdf,.docx,.doc,.txt"
                                onChange={onFileSelect}
                                className="hidden"
                            />
                            <label
                                htmlFor="file-upload"
                                className="flex flex-col items-center cursor-pointer"
                            >
                                <FileUp className="w-12 h-12 text-gray-400 mb-4" />
                                <span className="text-lg font-semibold text-brand-orange mb-2">
                                    {t('articleGenerator.chooseFiles')}
                                </span>
                                <span className="text-sm text-gray-500">
                                    {t('articleGenerator.dragDrop')}
                                </span>
                            </label>
                            {selectedFiles.length > 0 && (
                                <div className="mt-6 space-y-2 text-brand-dark">
                                    <h4 className="font-semibold text-sm">{t('articleGenerator.selectedFiles')}</h4>
                                    {selectedFiles.map((file, idx) => (
                                        <div key={idx} className="flex items-center justify-between bg-gray-50 rounded p-2">
                                            <div className="flex items-center overflow-hidden">
                                                <span className="text-sm truncate mr-2">{file.name}</span>
                                                <span className="text-xs text-gray-500 flex-shrink-0">
                                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => onRemoveFile(idx)}
                                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                                title={t('articleGenerator.removeFile', 'Remove file')}
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        onClick={onUpload}
                                        disabled={isUploading}
                                        className="w-full mt-4 px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center"
                                    >
                                        {isUploading ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                                {t('articleGenerator.creatingProjectAndUploading')}
                                            </>
                                        ) : (
                                            t('articleGenerator.uploadDocumentsTitle')
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Existing Logic for Article Plan & Pending Sections */}
            {project?.article_plan && (
                <div className="bg-blue-50 rounded-lg p-6">
                    <h3 className="font-semibold mb-2">{t('articleGenerator.articlePlan')}</h3>
                    <p className="text-sm text-brand-dark whitespace-pre-wrap">
                        <ReactMarkdown>{removeConsecutiveDuplicateLines(project.article_plan)}</ReactMarkdown>
                    </p>
                </div>
            )}

            {pendingSections.length > 0 && (
                <div className="mt-4 p-4 border border-blue-300 rounded-lg bg-blue-50">
                    <h4 className="font-semibold text-blue-800 mb-2">{t('articleGenerator.pendingSections')}</h4>
                    <ul className="space-y-2">
                        {pendingSections.map((sec, idx) => (
                            <li key={idx} className="flex items-start">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-200 text-blue-800 text-xs font-semibold mr-2 flex-shrink-0 mt-0.5">
                                    {idx + 1}
                                </span>
                                <div className="flex-1">
                                    <div className="font-medium text-blue-900">{sec.name}</div>
                                    <div className="text-xs text-blue-600 mt-0.5">
                                        Position: {
                                            typeof sec.insert_position === 'number'
                                                ? `After section ${sec.insert_position}`
                                                : sec.insert_position === 'start'
                                                ? 'At start'
                                                : 'At end'
                                        }
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <p className="text-xs text-blue-600 mt-3 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {t('articleGenerator.pendingSectionsInfo')}
                    </p>
                </div>
            )}
        </div>
    );
};

const ConfigureStep = ({
    project,
    onTitleChange,
    onAddSection,
    onUpdateSection,
    onDeleteSection,
    onNext,
    titleSuggestions,
    loadingTitles,
    showTitleSuggestions,
    onToggleTitleSuggestions,
    onSelectTitle,
    onRefreshTitles,
    t
}) => {
    const [newSectionName, setNewSectionName] = useState('');
    const [newSectionPrompt, setNewSectionPrompt] = useState('');
    const [showAddSection, setShowAddSection] = useState(false);
    const [insertPosition, setInsertPosition] = useState('end');
    const [insertAfterIndex, setInsertAfterIndex] = useState(0);
    const [hasCopiedPlan, setHasCopiedPlan] = useState(false);

    const totalSections = (project?.sections?.length || 0);
    const stripMarkdown = (text) => {
    if (!text) return '';
    
        return text
          // Remove code blocks first (including language specifier)
          .replace(/```[\s\S]*?```/g, '')
          // Remove inline code
          .replace(/`([^`]+)`/g, '$1')
          // Remove images
          .replace(/!\[([^\]]*)\]\([^\)]+\)/g, '$1')
          // Remove links but keep text
          .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
          // Remove bold
          .replace(/(\*\*|__)(.*?)\1/g, '$2')
          // Remove italic
          .replace(/(\*|_)(.*?)\1/g, '$2')
          // Remove strikethrough
          .replace(/~~(.*?)~~/g, '$1')
          // Remove headers
          .replace(/^#{1,6}\s+/gm, '')
          // Remove blockquotes
          .replace(/^\s{0,3}>\s?/gm, '')
          // Remove horizontal rules
          .replace(/^(-{3,}|\*{3,}|_{3,})$/gm, '')
          // Remove list markers
          .replace(/^\s*[-*+]\s+/gm, '')
          .replace(/^\s*\d+\.\s+/gm, '')
          // Remove HTML tags
          .replace(/<[^>]*>/g, '')
          // Clean up multiple newlines
          .replace(/\n{3,}/g, '\n\n')
          // Trim whitespace
          .trim();
      };

    const handleCopyPlan = async () => {
      try {
        await navigator.clipboard.writeText(stripMarkdown(project.article_plan) || '');
        setHasCopiedPlan(true);
        setTimeout(() => setHasCopiedPlan(false), 2000); // reset after 2s
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    };


    const handleAddSection = async () => {
        if (!newSectionName.trim()) return;

        let finalInsertPosition = insertPosition;
        if (insertPosition === 'after') {
            // Calculate the 1-based index after the selected section
            finalInsertPosition = insertAfterIndex + 1;
        }

        const sectionData = {
            name: newSectionName,
            prompt: newSectionPrompt || t('articleGenerator.defaultSectionPrompt'),
            // insert_position: insertPosition === 'after' ? insertAfterIndex + 1 : insertPosition
            insert_position: finalInsertPosition
        };

        try {
            // Call the parent function which handles API call and loadProject
            await onAddSection(sectionData); // Await the API call and reload

            // Only reset the form and close it *after* the API call succeeds
            setNewSectionName('');
            setNewSectionPrompt('');
            setInsertPosition('end');
            setInsertAfterIndex(0);
            setShowAddSection(false); // Close the form now

            // Optional: Clear any previous error state related to adding a section
            // setError(null); // If you have a local setError in ConfigureStep
        } catch (err) {
            // Optionally, keep the form open and show an error message
            // setError(err.message);
            console.error("Error adding section:", err);
            // The form remains open, user can try again or cancel
        }
    };
    const renderInsertPositionControls = () => {
        return (
            <div className="mb-3">
                <label className="block text-sm font-medium mb-1 text-brand-dark">
                    {t('articleGenerator.insertPositionLabel')}
                </label>
                <div className="flex gap-2">
                    <select
                        value={insertPosition}
                        onChange={(e) => setInsertPosition(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-brand-dark"
                    >
                        <option value="start">{t('articleGenerator.positionStart')}</option>
                        <option value="end">{t('articleGenerator.positionEnd')}</option>
                        {totalSections > 0 && <option value="after">{t('articleGenerator.positionAfter')}</option>}
                    </select>
                    {insertPosition === 'after' && (
                        <select
                            value={insertAfterIndex}
                            onChange={(e) => setInsertAfterIndex(Number(e.target.value))}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-brand-dark"
                        >
                            {project?.sections?.map((_, idx) => (
                                <option key={idx} value={idx}>
                                    {t('articleGenerator.sectionNumber', { number: idx + 1 })}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            </div>
        );
    };
    return (
        <div className="space-y-6">


            {project.article_plan && (
              <div className="space-y-6">
                {/* ---- Article Plan ---- */}
                <div className=" relative bg-blue-50 rounded-lg p-6">
                    <button
                      onClick={handleCopyPlan}
                      className="absolute top-2 right-2 p-1.5 bg-slate-200 hover:bg-brand-orange rounded-md transition-colors"
                      title={t('articleGenerator.copyPlan')}
                    >
                      {hasCopiedPlan ? (
                        <Check size={16} className="text-green-400" />
                      ) : (
                        <Copy size={16} className="text-gray-600" />
                      )}
                    </button>
                  <h3 className="font-semibold mb-2 text-brand-orange">
                    {t('articleGenerator.articlePlan')}
                  </h3>
                  <div className="text-sm text-brand-dark whitespace-pre-wrap">
                  <>
                    <ReactMarkdown>
                      {removeConsecutiveDuplicateLines(project.article_plan)}
                    </ReactMarkdown>
                    </>
                  </div>
                </div>

                {/* ---- Document Summaries (Expanders) ---- */}
                {project.documents?.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-brand-orange">
                      {t('articleGenerator.documentSummaries')}
                    </h3>
                    {project.documents.map((doc) => (
                    <>
                      <DocumentSummaryAccordion key={doc.id} doc={doc} t={t} />
                    </>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div>
                <h2 className="text-xl font-semibold mb-4 text-brand-orange">{t('articleGenerator.configureArticleTitle')}</h2>
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-brand-dark">{t('articleGenerator.articleTitle')}</label>
                        <button
                            onClick={onToggleTitleSuggestions}
                            disabled={loadingTitles}
                            className="flex items-center text-sm text-brand-orange hover:text-orange-600 disabled:opacity-50"
                        >
                            {loadingTitles ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                    {t('articleGenerator.generating')}
                                </>
                            ) : (
                                <>
                                    <Sparkles className="w-4 h-4 mr-1" />
                                    {t('articleGenerator.aiSuggestions')}
                                </>
                            )}
                        </button>
                    </div>
                    <input
                        type="text"
                        value={project.title}
                        onChange={(e) => onTitleChange(e.target.value)}
                        className="w-full text-brand-dark px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                        placeholder={t('articleGenerator.enterArticleTitle')}
                        disabled={!project}
                    />
                    {showTitleSuggestions && titleSuggestions.length > 0 && (
                        <div className="mt-2 border border-gray-200 rounded-lg bg-white shadow-lg p-3 space-y-2 text-brand-dark">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-gray-700">{t('articleGenerator.suggestedTitles')}</span>
                                <button
                                    onClick={onRefreshTitles}
                                    disabled={loadingTitles}
                                    className="text-sm text-brand-orange hover:text-orange-600 flex items-center"
                                >
                                    <RefreshCw className={`w-3 h-3 mr-1 ${loadingTitles ? 'animate-spin' : ''}`} />
                                    {t('articleGenerator.refresh')}
                                </button>
                            </div>
                            {titleSuggestions.map((suggestion, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => onSelectTitle(suggestion)}
                                    className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-brand-orange hover:text-white rounded transition-colors"
                                >
                                    {suggestion}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="mb-6">
                    <label className="block text-sm font-medium mb-2 text-brand-dark">{t('articleGenerator.citationStyle')}</label>
                    <select
                        value={project.citation_style}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md text-brand-dark"
                        disabled={!project}
                    >
                        <option value="APA">{t('articleGenerator.apa')}</option>
                        <option value="MLA">{t('articleGenerator.mla')}</option>
                        <option value="Chicago">{t('articleGenerator.chicago')}</option>
                        <option value="Harvard">{t('articleGenerator.harvard')}</option>
                        <option value="IEEE">{t('articleGenerator.ieee')}</option>
                    </select>
                </div>
            </div>
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-brand-orange">{t('articleGenerator.articleSections')}</h3>
                </div>
                <div className="space-y-3 text-brand-dark">
                    {project?.sections?.map((section, idx) => (
                        <SectionCard
                            key={section.id}
                            section={section}
                            index={idx}
                            onUpdate={onUpdateSection}
                            onDelete={onDeleteSection}
                            t={t}

                        />
                    ))}
                </div>
                <div className="flex items-center justify-between mb-4 mt-4">
                    <button
                        onClick={() => setShowAddSection(true)}
                        className="flex items-center px-3 py-1.5 text-sm bg-brand-orange text-white rounded-md hover:bg-orange-600"
                        disabled={!project} // Disable if no project yet
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('articleGenerator.addSection')}
                    </button>
                </div>
                {showAddSection && (
                    <div className="mt-4 p-4 border border-gray-300 rounded-lg bg-gray-50 text-brand-dark">
                        <h4 className="font-semibold mb-3">{t('articleGenerator.addNewSection')}</h4>
                        <input
                            type="text"
                            value={newSectionName}
                            onChange={(e) => setNewSectionName(e.target.value)}
                            placeholder={t('articleGenerator.sectionName')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
                        />
                        <textarea
                            value={newSectionPrompt}
                            onChange={(e) => setNewSectionPrompt(e.target.value)}
                            placeholder={t('articleGenerator.sectionInstructions')}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-3"
                        />
                        {renderInsertPositionControls()}
                        <div className="flex gap-2">
                            <button
                                onClick={handleAddSection} // Calls the modified function
                                className="px-4 py-2 bg-brand-orange text-white rounded-md hover:bg-orange-600"
                            >
                                {t('articleGenerator.add')}
                            </button>
                            <button
                                onClick={() => setShowAddSection(false)}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            >
                                {t('articleGenerator.cancel')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <button
                onClick={onNext}
                className="w-full px-6 py-3 bg-brand-orange text-white rounded-md hover:bg-orange-600 font-semibold"
                disabled={!project} // Disable if no project yet
            >
                {t('articleGenerator.proceedGenerate')}
            </button>
        </div>
    );
};

const SectionCard = ({ section, index, onUpdate, onDelete, t }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedPrompt, setEditedPrompt] = useState(section.prompt);

    const handleSave = () => {
        onUpdate(section.id, { prompt: editedPrompt });
        setIsEditing(false);
    };

    return (
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="flex items-start justify-between mb-2">
                <h4 className="font-semibold">
                    {index + 1}. {section.name}
                </h4>
                <div className="flex gap-2">
                    {section.section_type === 'llm_generated' && (
                        <button
                            onClick={() => setIsEditing(!isEditing)}
                            className="text-gray-500 hover:text-brand-orange"
                        >
                            <Edit className="w-4 h-4" />
                        </button>
                    )}
                    {section.name.toLowerCase() !== 'references' && (
                        <button
                            onClick={() => onDelete(section.id)}
                            className="text-gray-500 hover:text-red-500"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
            {isEditing ? (
                <div>
                    <textarea
                        value={editedPrompt}
                        onChange={(e) => setEditedPrompt(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md mb-2"
                    />
                    <div className="flex gap-2">
                        <button
                            onClick={handleSave}
                            className="px-3 py-1 text-sm bg-brand-orange text-white rounded hover:bg-orange-600"
                        >
                            {t('articleGenerator.save')}
                        </button>
                        <button
                            onClick={() => {
                                setEditedPrompt(section.prompt);
                                setIsEditing(false);
                            }}
                            className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                            {t('articleGenerator.cancel')}
                        </button>
                    </div>
                </div>
            ) : (
                <p className="text-sm text-gray-600">{section.prompt}</p>
            )}
        </div>
    );
};

const GenerateStep = ({ project, onGenerate, onDownload, onBack, t }) => {
    if (project.status === 'GENERATING') {
        return (
            <div className="border-2 border-brand-orange rounded-lg p-8 text-center">
                <Loader2 className="w-12 h-12 animate-spin text-brand-orange mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-brand-orange">{t('articleGenerator.generatingArticle')}</h3>
                {/* --- ADD PROGRESS BAR HERE --- */}
                <ProgressBar
                    progress={project.generation_progress || 0}
                    message={project.current_step}
                />
                <p className="text-gray-600">
                    {t('articleGenerator.generatingMessage')}
                </p>
            </div>
        );
    }

    if (project.status === 'COMPLETED' && project.generated_markdown) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between text-brand-dark">
                    <h2 className="text-xl font-semibold text-brand-orange">{t('articleGenerator.generatedArticle')}</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={() => onDownload('md')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            {t('articleGenerator.markdown')}
                        </button>
                        <button
                            onClick={() => onDownload('docx')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            {t('articleGenerator.word')}
                        </button>
                        <button
                            onClick={() => onDownload('html')}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            {t('articleGenerator.html')}
                        </button>
                    </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-8 bg-white text-brand-dark">
                    <ReactMarkdown >
                       {removeConsecutiveDuplicateLines(project.generated_markdown)}
                    </ReactMarkdown>
                </div>
                <button
                    onClick={onBack}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                    {t('articleGenerator.editConfiguration')}
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold mb-2 text-brand-orange">{t('articleGenerator.generateArticleTitle')}</h2>
                <p className="text-gray-600 mb-4">
                    {t('articleGenerator.reviewConfiguration')}
                </p>
                <div className="bg-gray-50 rounded-lg p-6 space-y-3 text-brand-dark">
                    <div className="flex justify-between">
                        <span className="font-medium">{t('articleGenerator.title')}:</span>
                        <span>{project.title}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium">{t('articleGenerator.documents')}:</span>
                        <span>{project.documents?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium">{t('articleGenerator.sections')}:</span>
                        <span>{project.sections?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="font-medium">{t('articleGenerator.citationStyle')}:</span>
                        <span>{project.citation_style}</span>
                    </div>
                </div>
            </div>
            <button
                onClick={onGenerate}
                className="w-full px-6 py-3 bg-brand-orange text-white rounded-md hover:bg-orange-600 font-semibold"
            >
                {t('articleGenerator.generateArticleButton')}
            </button>
        </div>
    );
};

export default ArticleGenerator;
